import { useEffect, useRef } from 'react';

interface MindbodyWidgetProps {
  /**
   * The full embed snippet the customer copied from their Mindbody dashboard —
   * the `<div class="mindbody-widget" ...></div><script ...></script>` block (or
   * the healcode equivalent). Injected verbatim.
   */
  embed: string;
}

/**
 * Renders a Mindbody widget by injecting its raw embed snippet directly into the page —
 * exactly as the customer's own site would. We use a contextual fragment (not
 * `innerHTML`/`dangerouslySetInnerHTML`) specifically because the embed contains a
 * `<script>` that must EXECUTE: scripts inserted via innerHTML never run, but those
 * parsed by `createContextualFragment` do when the fragment is appended.
 *
 * Mindbody's script then finds its `.mindbody-widget` div (or `<healcode-widget>`) and
 * upgrades it in place. Injecting the vendor snippet directly — rather than a nested
 * sandboxed iframe — keeps the brandedweb iframe-resizer handshake working, and means any
 * widget type/format Mindbody emits just works with no registry on our side.
 *
 * Note: brandedweb widgets render only from a domain allow-listed in the Mindbody dashboard.
 */
export const MindbodyWidget = ({ embed }: MindbodyWidgetProps): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;

    if (!container || !embed) {
      return undefined;
    }

    const fragment = document.createRange().createContextualFragment(embed);
    container.appendChild(fragment);

    return () => {
      container.replaceChildren();
    };
  }, [embed]);

  return <div ref={containerRef} data-testid="mindbody-widget" className="w-full" />;
};
